import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, X } from 'lucide-react';
import { generateInsights } from '../../utils/insightGenerator';

const EMOJI_MAP = {
  warning: '⚠️',
  tip: '💡',
  success: '✅'
};

const STYLE_MAP = {
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    btnBg: 'bg-amber-100',
    btnText: 'text-amber-800'
  },
  tip: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    btnBg: 'bg-blue-100',
    btnText: 'text-blue-800'
  },
  success: {
    bg: 'bg-income-50',
    border: 'border-income-200',
    btnBg: 'bg-income-100',
    btnText: 'text-income-800'
  }
};

const STORAGE_KEY = 'dashboard_dismissed_insights';

export default function ActionableInsights({ statsData, summary, recurringTransactions, formatAmount }) {
  const navigate = useNavigate();

  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    } catch { return []; }
  });

  const dismissInsight = useCallback((title) => {
    setDismissed(prev => {
      const next = [...prev, title];
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const insights = useMemo(() => {
    return generateInsights({ statsData, summary, recurringTransactions, formatAmount });
  }, [statsData, summary, recurringTransactions, formatAmount]);

  // Show top 2 insights, excluding dismissed ones
  const topInsights = insights
    .filter(i => !dismissed.includes(i.title))
    .slice(0, 2);

  if (topInsights.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold text-surface-900">Insights</h3>
        {insights.length > 0 && (
          <span className="w-5 h-5 rounded-full bg-expense-500 text-white text-[10px] font-bold flex items-center justify-center">
            {insights.filter(i => !dismissed.includes(i.title)).length}
          </span>
        )}
      </div>
      <div className={`flex gap-2.5 ${topInsights.length > 1 ? 'overflow-x-auto' : ''} pb-1 scrollbar-hide`}>
        {topInsights.map((insight, idx) => {
          const emoji = EMOJI_MAP[insight.type] || '💡';
          const style = STYLE_MAP[insight.type] || STYLE_MAP.tip;

          return (
            <div
              key={idx}
              className={`${topInsights.length === 1 ? 'w-full' : 'flex-shrink-0 w-[280px] md:w-auto md:flex-1'} rounded-xl p-3 border ${style.bg} ${style.border} relative`}
            >
              <button
                onClick={() => dismissInsight(insight.title)}
                className="absolute top-2 right-2 p-0.5 text-surface-400 hover:text-surface-600 rounded transition-colors"
                title="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-start gap-2.5 pr-4">
                <span className="text-lg flex-shrink-0">{emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-surface-900 leading-tight">{insight.title}</p>
                  <p className="text-xs text-surface-600 mt-0.5 leading-relaxed">{insight.description}</p>
                  {insight.action && (
                    <button
                      onClick={() => navigate(insight.action.path)}
                      className={`text-xs font-semibold mt-2 px-3 py-1.5 rounded-lg inline-flex items-center gap-1 ${style.btnBg} ${style.btnText}`}
                    >
                      {insight.action.label} →
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
